import { DefaultFile } from '@components/file-field/types';
import { JsonValue } from '@components/json-editor';
import {
  FieldMappingEditorValue,
  FieldType,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';
import { PickLiteral } from '@utils/types';

import { ConditionComparator, JourneyType } from 'api/types';
import { isInTemplateRoute } from 'ui/router';

import { request } from '../request';

type CommonIntegrationEndpointVariable<T extends string> = {
  name: string;
  type: T;
  label: string;
  template?: IntegrationEndpointVariable[];
  visible?: boolean;
  jqQuery?: string;
};

export type BaseVariableExample = string | null;
export type FileVariableExample = string | File | null;
export type ArrayVariableExample = null | object[];
export type BooleanVariableExample = boolean | null;

type IntegrationEndpointTypes = PickLiteral<
  FieldType,
  | 'email'
  | 'text'
  | 'number'
  | 'date'
  | 'paragraph'
  | 'decimal'
  | 'array_of_strings'
  | 'webhookUrl'
  | 'phone'
>;

export type IntegrationEndpointBaseVariable =
  CommonIntegrationEndpointVariable<IntegrationEndpointTypes> & {
    example?: BaseVariableExample;
    default_value?: BaseVariableExample;
  };

export type IntegrationEndpointDateVariable = CommonIntegrationEndpointVariable<'date'> & {
  example?: BaseVariableExample;
  default_value?: BaseVariableExample;
  format?: string;
};

export type IntegrationEndpointSelectVariable = CommonIntegrationEndpointVariable<'select'> & {
  example?: string;
  default_value?: string;
  options_from?: string;
  options: {
    value: string;
    label: string;
  }[];
};

type IntegrationEndpointFileVariable = CommonIntegrationEndpointVariable<'file'> & {
  example?: FileVariableExample;
  default_value?: FileVariableExample;
};

type IntegrationEndpointArrayVariable = CommonIntegrationEndpointVariable<'array'> & {
  example?: ArrayVariableExample;
  default_value?: ArrayVariableExample;
};

type IntegrationEndpointBooleanVariable = CommonIntegrationEndpointVariable<'boolean'> & {
  example?: BooleanVariableExample;
  default_value?: BooleanVariableExample;
};

export type IntegrationEndpointVariable =
  | IntegrationEndpointBaseVariable
  | IntegrationEndpointSelectVariable
  | IntegrationEndpointFileVariable
  | IntegrationEndpointBooleanVariable
  | IntegrationEndpointArrayVariable;

type BaseAuthentication = {
  shortId?: string;
  extraHeaders?: object;
  isAuthenticated: boolean;
};

export type OAuth2Authentication = BaseAuthentication & {
  authType: 'oauth2';
  details: {
    tokenUrl: string;
    clientId: string;
    clientSecret: string;
    grantType: string;
  };
};

export type BasicAuthentication = BaseAuthentication & {
  authType: 'basic';
  details: {
    username: string;
    password: string;
  };
};

export type BearerTokenAuthentication = BaseAuthentication & {
  authType: 'bearer_token';
  details: {
    clientId: string;
    clientSecret: string;
    url: string;
    tokenLocation: string;
    extraData: object;
    clientIdKey?: string;
    clientSecretKey?: string;
    prefix?: string;
    requestType?: 'json' | 'multipart';
  };
};

export type AuthorizationHeaderAuthentication = BaseAuthentication & {
  authType: 'authorization_header';
  details: {
    prefix: string;
    token: string;
  };
};

export type ApiKeyAuthentication = BaseAuthentication & {
  authType: 'api_key';
  details: {
    apiKey: string;
    headerKey: string;
  };
};

export type JwtTokenAuthentication = BaseAuthentication & {
  authType: 'jwt_token';
  details: {
    issuer: string;
    key: string;
    secret: string;
    expirationSeconds?: number;
    subject?: string;
    prefix?: string;
  };
};

export type NoAuthentication = BaseAuthentication & {
  authType: '';
  details: {};
};

export type Authentication =
  | OAuth2Authentication
  | BasicAuthentication
  | BearerTokenAuthentication
  | AuthorizationHeaderAuthentication
  | ApiKeyAuthentication
  | JwtTokenAuthentication
  | NoAuthentication;

export type AuthenticationType = Authentication['authType'];

type EmptyFieldHandling = 'empty_string' | 'null' | 'skip';

export type Integration = {
  shortId: string;
  name: string;
  description?: string;
  logo?: string;
  thumbnail?: string;
  authentication: Authentication | null;
  endpoints: Array<IntegrationEndpoint<string>>;
  webhooks: Array<IntegrationWebhook>;
  numJourneys: number;
  emptyFieldHandling: EmptyFieldHandling;
};

export type IntegrationAuthenticationType = {
  authenticationShortId?: string;
  authentication?: Authentication;
};

export const loadIntegrations = async (queryString: string, isTested: boolean) => {
  const url = isTested ? 'v1/integrations/?is_tested=1/' : 'v1/integrations/';
  const response = await request<Integration[]>(`${url}${queryString}`);
  return response.data;
};

export const loadIntegration = async (shortId: string) => {
  const response = await request<Integration>(`v1/integrations/${shortId}/`);
  return response.data;
};

export const loadIntegrationEndpoint = async (shortId: string) => {
  const url = isInTemplateRoute()
    ? `v1/templates/endpoints/${shortId}/`
    : `v1/integrations/endpoint/${shortId}/`;
  const response = await request<IntegrationEndpointDetail>(url);
  return response.data;
};

export const deleteIntegrationEndpoint = async (shortId: string) => {
  const response = await request.delete(`v1/integrations/endpoint/${shortId}/`);
  return response.data;
};

export type IntegrationBase = {
  name?: string;
  description?: string;
  logo?: File | null;
  emptyFieldHandling: EmptyFieldHandling;
};

export const createIntegration = async (data: IntegrationBase) => {
  const response = await request.postForm('v1/integrations/', data);
  return response.data;
};

export const editIntegration = async ({
  integrationId,
  data,
}: {
  integrationId: Integration['shortId'];
  data: IntegrationBase;
}) => {
  const response = await request.patchForm(`v1/integrations/${integrationId}/`, data);
  return response.data;
};

export const deleteIntegration = async (integrationId: Integration['shortId']) => {
  const response = await request.delete(`v1/integrations/${integrationId}/`);
  return response.data;
};

export const loadIntegrationJourneys = async (shortId: string) => {
  const response = await request<JourneyType[]>(`v1/integrations/${shortId}/journeys/`);
  return response.data;
};

export const endpointMethods = ['POST', 'GET', 'PATCH', 'PUT', 'DELETE'] as const;
export type EndpointMethods = (typeof endpointMethods)[number];

export const requestTypes = ['json', 'multipart', 'graphql', 'file'] as const;
export type RequestTypes = (typeof requestTypes)[number];

export type IntegrationEndpoint<T extends Integration | string = Integration> = {
  shortId: string;
  name: string;
  method: EndpointMethods;
  requestType: (typeof requestTypes)[number];
  url: string;
  variables: IntegrationEndpointVariable[];
  responseValues: IntegrationEndpointVariable[];
  integration: T;
  numJourneys: number;
  exampleData: JsonValue;
  exampleResponseData?: JsonValue;
  graphqlRequest: string;
  schemaUrl?: string;
  exampleFiles?: ExampleFileType[];
  defaultFiles?: DefaultFileType[];
};

type ExampleFileType = DefaultFile & {
  variableName: string;
};

export type DefaultFileType = DefaultFile & {
  variableName: string;
};

export type IntegrationEndpointDetail = IntegrationEndpoint & { thumbnail?: string };

export type IntegrationEndpointCreateType = IntegrationEndpoint<string> & {
  integration: Integration['shortId'];
  exampleData: string;
};

export const createIntegrationEndpoint = async (data: IntegrationEndpointCreateType) => {
  const response = await request.post(`v1/integrations/endpoint/`, {
    ...data,
    exampleData: data.exampleData ? JSON.parse(data.exampleData) : undefined,
  });
  return response.data;
};

export type IntegrationEndpointUpdateType = {
  variables?: IntegrationEndpointVariable[];
  exampleData?: string;
  responseValues?: IntegrationEndpoint['responseValues'];
};

export const updateIntegrationEndpoint = async (
  shortId: string,
  data: IntegrationEndpointUpdateType
) => {
  const response = await request.patch(`v1/integrations/endpoint/${shortId}/`, {
    ...data,
    exampleData: data.exampleData ? JSON.parse(data.exampleData) : undefined,
  });
  return response.data;
};

export const updateIntegrationEndpointExampleFiles = async (shortId: string, data: FormData) => {
  const response = await request.post(`v1/integrations/endpoint/${shortId}/files/`, data);
  return response.data;
};

export const updateIntegrationEndpointDefaultFiles = async (shortId: string, data: FormData) => {
  const response = await request.post(`v1/integrations/endpoint/${shortId}/default-files/`, data);
  return response.data;
};

export type OAuth2Details = {
  authType: 'oauth2';
  tokenUrl: string;
  clientId: string;
  clientSecret: string;
};

export type BasicDetails = {
  authType: 'basic';
  username: string;
  password: string;
};

export type BearerTokenDetails = {
  authType: 'bearer_token';
  clientId: string;
  clientSecret: string;
  url: string;
  tokenLocation: string;
};

export type AuthorizationHeaderDetails = {
  authType: 'authorization_header';
  prefix: string;
  token: string;
};

export type ApiKeyDetails = {
  authType: 'api_key';
  apiKey: string;
  headerKey: string;
};

type NoDetails = {
  authType: '';
};

export type Details =
  | OAuth2Details
  | BasicDetails
  | BearerTokenDetails
  | AuthorizationHeaderDetails
  | ApiKeyDetails
  | NoDetails;

export const updateIntegrationAuthentication = async (shortId: string, data: Authentication) => {
  const response = await request.patch(`v1/integrations/${shortId}/`, { authentication: data });
  return response.data;
};

export const integrationTestRequest = async (
  shortId: string,
  data: { variables: IntegrationEndpointVariable[] }
) => {
  const response = await request.post<IntegrationTestRequestType>(
    `v1/integrations/endpoint/${shortId}/test-request/`,
    data
  );
  return response.data;
};

export type IntegrationTestRequestType = {
  success: boolean;
  statusCode: number;
  data: object;
};

export type AuthenticationTestType = {
  authType: AuthenticationType;
  details: Authentication['details'];
};

export const authenticationTest = async (data: AuthenticationTestType) => {
  const response = await request.post('v1/integrations/authentication/test/', data);
  return response.data;
};

export type IntegrationEndpointMinimal = {
  shortId: string;
  name: string;
  thumbnail?: string;
};

export const loadJourneyIntegrationEndpoints = async (shortId: string) => {
  const url = isInTemplateRoute()
    ? `v1/templates/journey/${shortId}/endpoints/`
    : `v1/integrations/journey/${shortId}/endpoints/`;
  const response = await request<IntegrationEndpointMinimal[]>(url);

  return response.data;
};

export const loadJourneyActionIntegrationEndpoint = async (shortId: string) => {
  const response = await request<IntegrationEndpointMinimal>(
    `v1/integrations/journey-action/${shortId}/endpoints/`
  );
  return response.data;
};

export const loadTravellerJourneyIntegrationEndpoint = async (shortId: string) => {
  const response = await request<IntegrationEndpointMinimal[]>(
    `v1/integrations/traveller-journey/${shortId}/endpoints/`
  );
  return response.data;
};
export const loadTravellerActionIntegrationEndpoint = async (shortId: string) => {
  const response = await request<IntegrationEndpointMinimal>(
    `v1/integrations/traveller-action/${shortId}/endpoints/`
  );
  return response.data;
};

export type WebhookCondition = {
  id: string;
  field?: VariableValue;
  comparator?: ConditionComparator;
  value?: string | FieldMappingEditorValue;
  visible?: boolean;
};

export type IntegrationWebhook = {
  shortId: string;
  name: string;
  method: EndpointMethods;
  url: string;
  uniqueUrl: string;
  webhookCreated: boolean;
  requestData: object;
  variables: IntegrationEndpointVariable[];
  payloadVariables: IntegrationEndpointVariable[];
  responseValues: IntegrationEndpointVariable[];
  integration: string;
  exampleData: JsonValue;
  payloadData: JsonValue;
  conditions: WebhookCondition[];
  isSubscribable: boolean;
  thumbnail?: string;
};

export const getIntegrationWebhook = async (shortId: string) => {
  const url = isInTemplateRoute()
    ? `v1/templates/webhooks/${shortId}/`
    : `v1/integrations/webhook/${shortId}/`;
  const response = await request.get<IntegrationWebhook>(url);
  return response.data;
};

export type IntegrationWebhookCreateType = IntegrationWebhook & {
  integration: Integration['shortId'];
  exampleData: string;
  payloadData: string;
  isSubscribable: boolean;
};

export const createIntegrationWebhook = async (data: IntegrationWebhookCreateType) => {
  const response = await request.post(`v1/integrations/webhook/`, {
    ...data,
    exampleData: data.exampleData ? JSON.parse(data.exampleData) : undefined,
    payloadData: data.payloadData ? JSON.parse(data.payloadData) : undefined,
  });
  return response.data;
};

export type IntegrationWebhookUpdateType = {
  variables?: IntegrationEndpointVariable[];
  payloadVariables?: IntegrationEndpointVariable[];
  exampleData?: string;
  payloadData?: string;
  conditions?: WebhookCondition[];
};

export const updateIntegrationWebhook = async (
  shortId: string,
  data: IntegrationWebhookUpdateType
) => {
  const response = await request.patch(`v1/integrations/webhook/${shortId}/`, {
    ...data,
    exampleData: data.exampleData ? JSON.parse(data.exampleData) : undefined,
    payloadData: data.payloadData ? JSON.parse(data.payloadData) : undefined,
  });
  return response.data;
};

export type IntegrationWebhookCreateRequestType = {
  success: boolean;
  statusCode: number;
  data: object;
};

export const subscribeIntegrationWebhook = async (shortId: string, data: object) => {
  const response = await request.post<IntegrationWebhookCreateRequestType>(
    `v1/integrations/webhook/${shortId}/subscribe/`,
    data
  );
  return response.data;
};

export type IntegrationWebhookCreateRemoveType = {
  url: string;
  method: EndpointMethods;
  body: string;
};

export const unsubscribeIntegrationWebhook = async (
  shortId: string,
  data: IntegrationWebhookCreateRemoveType
) => {
  const response = await request.post<IntegrationWebhookCreateRequestType>(
    `v1/integrations/webhook/${shortId}/unsubscribe/`,
    {
      ...data,
      body: data.body ? JSON.parse(data.body) : undefined,
    }
  );
  return response.data;
};

export type IntegrationTravellerActionFile = {
  file?: string;
};

export const deleteIntegrationWebhook = async (shortId: string) => {
  const response = await request.delete(`v1/integrations/webhook/${shortId}/`);
  return response.data;
};

export type IntegrationWebhookMinimal = {
  shortId: string;
  name: string;
  thumbnail?: string;
};

export const loadJourneyIntegrationWebhooks = async (shortId: string) => {
  const url = isInTemplateRoute()
    ? `v1/templates/journey/${shortId}/webhooks/`
    : `v1/integrations/journey/${shortId}/webhooks/`;
  const response = await request<IntegrationWebhookMinimal[]>(url);
  return response.data;
};

export const loadTravellerJourneyIntegrationWebhook = async (shortId: string) => {
  const response = await request<IntegrationWebhookMinimal[]>(
    `v1/integrations/traveller-journey/${shortId}/webhooks/`
  );
  return response.data;
};

export type IntegrationWebhookLogs = {
  dateCreated: string;
  shortId: string;
  requestData: object;
};

export const loadIntegrationWebhookLogs = async (shortId: string) => {
  const response = await request<IntegrationWebhookLogs[]>(
    `v1/integrations/webhook/${shortId}/logs/`
  );
  return response.data;
};

export const loadIntegrationTravellerActionFile = async (shortId: string) => {
  const response = await request<IntegrationTravellerActionFile>(
    `v1/integrations/traveller-action/${shortId}/file/`
  );
  return response.data;
};

export type ListSyncTestRequestType = Omit<IntegrationTestRequestType, 'data'> & {
  exampleData: object;
  arrayLocation: string;
  mappingKeys: string[];
  error?: string;
};

export const ListSyncTestRequest = async (integrationShortId: string, data: { url: string }) => {
  const response = await request.post<ListSyncTestRequestType>(
    `v1/integrations/${integrationShortId}/test-request/`,
    data
  );
  return response.data;
};

export type DuplicateEndpointData = {
  name: string;
};

export const duplicateIntegrationEndpoint = async (
  shortId: string,
  data: DuplicateEndpointData
) => {
  const response = await request.post(`v1/integrations/endpoint/${shortId}/duplicate/`, data);
  return response.data;
};

export const duplicateIntegrationWebhook = async (shortId: string, data: DuplicateEndpointData) => {
  const response = await request.post(`v1/integrations/webhook/${shortId}/duplicate/`, data);
  return response.data;
};

export type DuplicateIntegrationData = {
  name: string;
};

export const duplicateIntegration = async (shortId: string, data: DuplicateIntegrationData) => {
  const response = await request.post(`v1/integrations/${shortId}/duplicate/`, data);
  return response.data;
};

export type EditPollingVisibilityType = {
  visibleIds: string[];
};

export const EditPollingVisibility = async (data: EditPollingVisibilityType) => {
  const response = await request.post(`v1/integrations/polling-visibility/`, data);
  return response.data;
};

export type IntegrationEndpointPollingType = Pick<
  IntegrationEndpoint<string>,
  'shortId' | 'name' | 'method' | 'url' | 'variables'
> & {
  showInPolling: boolean;
  isValidPollingEndpoint: boolean;
  isUsedInJourney: boolean;
};

export type IntegrationPollingType = Pick<
  Integration,
  'shortId' | 'name' | 'description' | 'logo' | 'thumbnail'
> & {
  endpoints: IntegrationEndpointPollingType[];
};

export const listPollingEndpoints = async (journeyId: string) => {
  const response = await request.get<IntegrationPollingType[]>(
    `v1/integrations/polling-visibility/?journey_id=${journeyId}`
  );

  return response.data;
};

export type ValidateJqQueryParams = {
  query: string;
  exampleData: object;
};

export type ValidateJQQueryResponse = {
  success: boolean;
  result?: any;
  error?: string;
};

export const validateJqQuery = async (data: ValidateJqQueryParams) => {
  const response = await request.post<ValidateJQQueryResponse>(
    'v1/integrations/validate-jq/',
    data
  );
  return response.data;
};

export type GenerateJqQueryParams = {
  prompt: string;
  exampleData: object;
};

export type GenerateJQQueryResponse = {
  success: boolean;
  query?: string;
  result?: any;
  suggestedType?: string;
  error?: string;
};

export const generateJqQuery = async (data: GenerateJqQueryParams) => {
  const response = await request.post<GenerateJQQueryResponse>(
    'v1/integrations/generate-jq/',
    data
  );
  return response.data;
};
